import { useState } from "react";
import { Button, ButtonVariant, Text, Tooltip } from "@merge-api/merge-javascript-shared";
import { Bot, CircleCheck, RefreshCw, X } from "lucide-react";
import { usePublishModuleContext } from "../../context/PublishModuleContext";
import useCheckAutomatedTestRegistry from "../hooks/useCheckAutomatedTestRegistry";
import { TestExecutionStatus, AutomatedTestSuiteExecution } from "../types";
import PublishModuleOverrideChecksModal from "../../IntegrationStagedVersions/PublishModuleOverrideChecksModal";

interface Props {
  automatedTestSuiteExecution: AutomatedTestSuiteExecution | null;
}

const areAllTestsFinished = (
  automatedTestSuiteExecution: AutomatedTestSuiteExecution | null
): boolean => {
  if (!automatedTestSuiteExecution || Object.keys(automatedTestSuiteExecution.tests).length === 0) {
    return false;
  }

  const testStatuses = Object.values(automatedTestSuiteExecution.tests).map((test) => test.status);

  return testStatuses.every(
    (status) =>
      status === TestExecutionStatus.SUCCEEDED ||
      status === TestExecutionStatus.FAILED ||
      status === TestExecutionStatus.ABORTED
  );
};

const getStatusIcon = (
  automatedTestSuiteExecution: AutomatedTestSuiteExecution | null
): JSX.Element => {
  if (!automatedTestSuiteExecution || Object.keys(automatedTestSuiteExecution.tests).length === 0) {
    return <Bot size={16} className="text-gray-40" />;
  }

  const testStatuses = Object.values(automatedTestSuiteExecution.tests).map((test) => test.status);

  if (
    testStatuses.some(
      (status) =>
        status === TestExecutionStatus.PENDING || status === TestExecutionStatus.IN_PROGRESS
    )
  ) {
    return <RefreshCw className="rotating text-blue-60" size={16} />;
  }

  if (
    testStatuses.some(
      (status) => status === TestExecutionStatus.FAILED || status === TestExecutionStatus.ABORTED
    )
  ) {
    return <X size={16} className="text-red-70" />;
  }

  if (testStatuses.every((status) => status === TestExecutionStatus.SUCCEEDED)) {
    return <CircleCheck size={16} className="text-teal-70" />;
  }

  return <Bot size={16} className="text-gray-40" />;
};

const AutomatedTestAccordionTitleV2 = ({ automatedTestSuiteExecution }: Props) => {
  // state
  const [isOverrideModalOpen, setIsOverrideModalOpen] = useState(false);

  // hooks
  const {
    isRunningAutomatedTests,
    onRunAutomatedTests,
    integrationID,
    areAutomatedTestsFailing,
    setOverrideComment,
    setOverrideTicket,
    setAreAutomatedTestsFailing,
    setHasUsedAutomatedTestOverride,
  } = usePublishModuleContext();
  const { hasTests, loading } = useCheckAutomatedTestRegistry({ integrationID });

  // derived state
  const allTestsFinished = areAllTestsFinished(automatedTestSuiteExecution);
  const statusIcon = getStatusIcon(automatedTestSuiteExecution);

  // event handlers
  const handleRunClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRunAutomatedTests?.();
  };

  const handleOverrideClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOverrideModalOpen(true);
  };

  // render

  return (
    <>
      <PublishModuleOverrideChecksModal
        open={isOverrideModalOpen}
        onClose={() => setIsOverrideModalOpen(false)}
        integrationID={integrationID}
        setOverrideComment={setOverrideComment}
        setOverrideTicket={setOverrideTicket}
        setCanPublish={(canPublish: boolean) => {
          setAreAutomatedTestsFailing(!canPublish);
          if (canPublish) {
            setHasUsedAutomatedTestOverride(true);
          }
        }}
      />
      <div className="flex flex-row justify-between w-full">
        <div className="flex flex-row justify-start items-center space-x-3">
          {statusIcon}
          <Text variant="h5" className="text-black">
            Automated tests
          </Text>
        </div>
        <div className="mr-6 flex items-center space-x-2">
          {areAutomatedTestsFailing && (
            <Button variant={ButtonVariant.TextBlack} size="sm" onClick={handleOverrideClick}>
              Override
            </Button>
          )}
          {!allTestsFinished && (
            <>
              {isRunningAutomatedTests ? (
                <Button
                  size="sm"
                  variant={ButtonVariant.TertiaryWhite}
                  leftIcon={<RefreshCw className="rotating" size={12} />}
                  disabled
                >
                  Running
                </Button>
              ) : !hasTests && !loading ? (
                <Tooltip title="No tests available in automated test repository">
                  <Button size="sm" variant={ButtonVariant.TertiaryWhite} disabled>
                    Run
                  </Button>
                </Tooltip>
              ) : (
                <Button size="sm" variant={ButtonVariant.TertiaryWhite} onClick={handleRunClick}>
                  Run
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default AutomatedTestAccordionTitleV2;
